import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faExternalLinkAlt,
  faHistory,
  faSpinner,
  faShieldAlt,
  faTrashCan,
  faPlus,
  faLink,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { usePrivy } from "@privy-io/react-auth";
import useConnectedWallet from "../hooks/useConnectedWallet";
import { Helmet } from "react-helmet";
import "../scss/index.scss";
import {
  getActiveWallet,
  getOwnWalletAudit,
  updatePartnerWallet,
  listPartnerWallets,
  addPartnerWallet,
  removePartnerWallet,
  getVolumes,
  getTransactions,
  changePage,
} from "../store/actions";

const shortHash = (addr) => {
  if (!addr || typeof addr !== "string") return "—";
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
};

const isEvmAddress = (addr) =>
  typeof addr === "string" && /^0x[a-fA-F0-9]{40}$/.test(addr);

const WalletCard = ({
  title,
  address,
  description,
  onViewHistory,
  actionSlot,
  emptyHint,
}) => {
  const copy = () => {
    if (!address) return;
    navigator.clipboard
      .writeText(address)
      .then(() => toast.success("Copied to clipboard"))
      .catch(() => toast.error("Failed to copy"));
  };

  return (
    <div className="lwCard">
      <div className="lwCardHd">
        <div>
          <div className="lwCardT">{title}</div>
          <div className="lwCardD">{description}</div>
        </div>
        <button className="btnGhost sm" onClick={onViewHistory} type="button">
          <FontAwesomeIcon icon={faHistory} /> View history
        </button>
      </div>

      <div className={`lwAddrBox${address ? "" : " muted"}`} title={address || ""}>
        {address ? shortHash(address) : emptyHint || "Not set"}
        {address && (
          <div className="acts">
            <button
              className="btnGhost sm"
              onClick={copy}
              type="button"
              title="Copy full address"
            >
              <FontAwesomeIcon icon={faCopy} /> Copy
            </button>
            {isEvmAddress(address) && (
              <a
                href={`https://etherscan.io/address/${address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btnGhost sm"
                title="View on Etherscan"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} /> Explorer
              </a>
            )}
          </div>
        )}
      </div>

      {actionSlot && <div style={{ marginTop: 4 }}>{actionSlot}</div>}
    </div>
  );
};

const HistoryModal = ({ open, title, onClose, rows, loading, filterField }) => {
  if (!open) return null;

  const list = Array.isArray(rows) ? rows : [];
  const filtered = filterField
    ? list.filter((r) => (r.field_changed || "").includes(filterField))
    : list;

  return (
    <div className="iswModalOverlay" onClick={onClose}>
      <div
        className="iswModal"
        style={{ maxWidth: 720 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
            gap: 12,
          }}
        >
          <h3 className="mTitle" style={{ margin: 0 }}>
            {title}
          </h3>
          <button className="btnGhost sm" onClick={onClose} type="button">
            Close
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 20, color: "#a7b2ab" }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading history…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 20, color: "#7c847e" }}>
            No history to show yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((row) => (
              <div
                key={row.id}
                style={{
                  background: "#0d0e0e",
                  border: "1px solid rgba(255,255,255,.08)",
                  borderRadius: 11,
                  padding: 13,
                }}
              >
                <div style={{ fontSize: 12, color: "#7c847e", marginBottom: 6 }}>
                  {row.changed_at} · {row.changed_by || "unknown source"} ·{" "}
                  {row.caller_ip || "no ip"}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: "#eef3f0",
                    wordBreak: "break-all",
                  }}
                >
                  <strong>{row.field_changed}</strong>:{" "}
                  {shortHash(row.old_value) || "∅"} →{" "}
                  {shortHash(row.new_value) || "∅"}
                </div>
                {row.notes && (
                  <div style={{ fontSize: 12, color: "#7c847e", marginTop: 6 }}>
                    {row.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LinkedWallets = () => {
  const dispatch = useDispatch();
  const { linkWallet } = usePrivy();
  const { address: connectedAddress, isConnected } = useConnectedWallet();

  const user = useSelector((state) => state.userReducer.user);
  const activeWallet = useSelector((state) => state.userReducer.activeWallet);
  const walletAudit = useSelector((state) => state.userReducer.walletAudit);
  const walletAuditLoading = useSelector(
    (state) => state.userReducer.walletAuditLoading
  );
  const partnerWallets = useSelector(
    (state) => state.userReducer.partnerWallets
  );
  const partnerWalletsLoading = useSelector(
    (state) => state.userReducer.partnerWalletsLoading
  );

  const auditItems = Array.isArray(walletAudit)
    ? walletAudit
    : walletAudit?.items || [];

  const loginWallet = user?.response?.walletAddress || "";

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTitle, setHistoryTitle] = useState("Wallet history");
  const [historyFilter, setHistoryFilter] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [manualAddress, setManualAddress] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState({});

  useEffect(() => {
    dispatch(getActiveWallet());
    dispatch(getOwnWalletAudit());
    dispatch(listPartnerWallets());
  }, [dispatch]);

  const refreshAfterChange = () => {
    dispatch(listPartnerWallets());
    dispatch(getOwnWalletAudit());
    dispatch(getVolumes());
    dispatch(getTransactions());
  };

  const handleManualAdd = async () => {
    const addr = manualAddress.trim();
    if (!addr) {
      toast.error("Enter a wallet address");
      return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) {
      toast.error("That doesn't look like a valid EVM wallet address");
      return;
    }
    const already = (partnerWallets || []).some(
      (w) => (w.wallet || "").toLowerCase() === addr.toLowerCase()
    );
    if (already) {
      toast.info("That wallet is already linked.");
      return;
    }
    const code = (manualCode || "").trim();
    if (!/^[0-9]{6}$/.test(code)) {
      toast.error("Enter your 6-digit 2FA code to link a wallet.");
      return;
    }
    setAdding(true);
    try {
      await dispatch(addPartnerWallet(addr, "manual", code));
      toast.success(`Linked ${shortHash(addr)} to your partner profile.`);
      setManualAddress("");
      setManualCode("");
      refreshAfterChange();
    } catch (err) {
      if (err.status === 409 && err.conflictingPartner) {
        toast.error(
          `This wallet is already linked to partner ${err.conflictingPartner}.`
        );
      } else {
        toast.error(err.message || "Failed to link wallet");
      }
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (wallet) => {
    const confirmed = window.confirm(
      `Unlink ${wallet} from your partner profile?\n\n` +
        `Stats from this wallet will no longer aggregate into your dashboard.`
    );
    if (!confirmed) return;

    setRemoving((prev) => ({ ...prev, [wallet]: true }));
    try {
      await dispatch(removePartnerWallet(wallet));
      toast.success(`Unlinked ${shortHash(wallet)}.`);
      refreshAfterChange();
    } catch (err) {
      if (err.status === 409) {
        toast.error(
          err.message ||
            "Cannot remove the canonical login wallet. Use 'Update login wallet' instead."
        );
      } else {
        toast.error(err.message || "Failed to unlink wallet");
      }
    } finally {
      setRemoving((prev) => {
        const next = { ...prev };
        delete next[wallet];
        return next;
      });
    }
  };

  const openHistory = (title, filter) => {
    setHistoryTitle(title);
    setHistoryFilter(filter);
    setHistoryOpen(true);
  };

  const handleRelink = async () => {
    // No wallet on the Privy identity yet: let them attach one (proves ownership).
    if (!isConnected || !connectedAddress) {
      try {
        await linkWallet();
      } catch (e) {
        // Privy handles its own errors
      }
      return;
    }

    // Prevent no-op relink to the same address
    if (
      loginWallet &&
      connectedAddress.toLowerCase() === loginWallet.toLowerCase()
    ) {
      toast.info("This wallet is already your login wallet.");
      return;
    }

    const confirmed = window.confirm(
      `Relink your CMS login wallet to ${connectedAddress}?\n\n` +
        `A confirmation email will be sent. Your withdrawal wallet will NOT change.`
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      await dispatch(updatePartnerWallet(connectedAddress));
      toast.success("Login wallet updated. Check your email for confirmation.");
      refreshAfterChange();
    } catch (err) {
      if (err.status === 409 && err.conflictingPartner) {
        toast.error(
          `This wallet is already linked to partner ${err.conflictingPartner}. Contact support.`
        );
      } else if (err.status === 404 || /not\s*found/i.test(err.message)) {
        toast.info(
          "Login-wallet relink is coming soon. The backend endpoint is not live yet."
        );
      } else {
        toast.error(err.message || "Failed to update login wallet");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Linked Wallets | Instaswap Partners</title>
      </Helmet>

      <div>
        <div
          className="secHdr"
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 400px" }}>
            <h2 className="secTitle">Linked Wallets</h2>
            <p className="secLead">
              Manage the wallets attached to your partner profile. The{" "}
              <b style={{ color: "#dfe6e0" }}>login wallet</b> identifies you in
              the dashboard and for referral tracking. The{" "}
              <b style={{ color: "#dfe6e0" }}>withdrawal wallet</b> is where
              payouts are sent.
            </p>
          </div>
          <Link to="/security" className="btnGhost sm" style={{ flex: "none" }}>
            <FontAwesomeIcon icon={faShieldAlt} /> Security events
          </Link>
        </div>

        <div className="notice" style={{ maxWidth: 860, marginBottom: 22 }}>
          <FontAwesomeIcon icon={faTriangleExclamation} className="ni" />
          <div>
            The wallets listed here are only used to <b>sign in</b> and to track
            your referrals. They are <b>not</b> your withdrawal/payout wallet.
            To change where your USDC payouts are sent, use{" "}
            <b>Withdraw Wallet</b> in the menu.
          </div>
        </div>

        <div className="lwCol">
          <WalletCard
            title="Login wallet"
            description="Used to sign in to the dashboard and to track referrals. Changing this does not change your withdrawal wallet."
            address={loginWallet}
            emptyHint="No login wallet linked yet"
            onViewHistory={() =>
              openHistory("Login wallet history", "walletAddress")
            }
            actionSlot={
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div className="lwNotes">
                  · Changing your login wallet does <b>not</b> change your
                  withdrawal wallet.
                  <br />
                  · Your referral link stays the same.
                  <br />
                  · A notification email will be sent to confirm the change.
                </div>

                {isConnected && connectedAddress && (
                  <div style={{ fontSize: 12.5, color: "#7c847e" }}>
                    Connected wallet:{" "}
                    <code
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        color: "#3ad17e",
                      }}
                    >
                      {shortHash(connectedAddress)}
                    </code>
                  </div>
                )}

                <button
                  onClick={handleRelink}
                  disabled={submitting}
                  type="button"
                  className="btnPrimary sm"
                  style={{ alignSelf: "flex-start" }}
                >
                  {submitting ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : isConnected && connectedAddress ? (
                    <>Relink login wallet to {shortHash(connectedAddress)}</>
                  ) : (
                    <>Connect a wallet to relink</>
                  )}
                </button>
              </div>
            }
          />

          <div className="lwCard">
            <div className="lwCardHd">
              <div>
                <div className="lwCardT">All linked wallets</div>
                <div className="lwCardD">
                  Stats from every linked wallet (referrals, points, swaps) are
                  aggregated into your dashboard.
                </div>
              </div>
              {partnerWalletsLoading && (
                <span style={{ fontSize: 13, color: "#7c847e" }}>
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading…
                </span>
              )}
            </div>

            {(!partnerWallets || partnerWallets.length === 0) &&
            !partnerWalletsLoading ? (
              <div className="lwAddrBox muted">
                No additional wallets linked yet. Use the form below or accept
                the dashboard banner when prompted.
              </div>
            ) : (
              <div className="gtScroll" style={{ margin: "16px 0" }}>
                <div style={{ minWidth: 560 }}>
                  <div
                    className="gtHead"
                    style={{
                      gridTemplateColumns: "minmax(190px,1.4fr) 130px 170px 120px",
                    }}
                  >
                    <span>Wallet</span>
                    <span>Source</span>
                    <span>Linked at</span>
                    <span className="r">Actions</span>
                  </div>
                  {(partnerWallets || []).map((row) => {
                    const wallet = row.wallet;
                    const isCanonical =
                      row.isCanonical ||
                      (loginWallet &&
                        wallet &&
                        wallet.toLowerCase() === loginWallet.toLowerCase());
                    const isRemoving = !!removing[wallet];
                    return (
                      <div
                        key={wallet}
                        className="gtRow"
                        style={{
                          gridTemplateColumns:
                            "minmax(190px,1.4fr) 130px 170px 120px",
                          minHeight: 56,
                        }}
                      >
                        <div
                          className="cellMono"
                          style={{ color: "#eef3f0" }}
                          title={wallet}
                        >
                          {shortHash(wallet)}
                          {isCanonical && (
                            <span
                              className="shVia"
                              style={{ marginLeft: 8 }}
                              title="Canonical login wallet"
                            >
                              LOGIN
                            </span>
                          )}
                        </div>
                        <div className="cellStrong">{row.source || "—"}</div>
                        <div className="cellDate" style={{ color: "#7c847e" }}>
                          {row.linkedAt || row.linked_at || "—"}
                        </div>
                        <div className="r">
                          <button
                            type="button"
                            disabled={isCanonical || isRemoving}
                            onClick={() => handleRemove(wallet)}
                            title={
                              isCanonical
                                ? "Cannot remove the canonical login wallet"
                                : "Unlink this wallet"
                            }
                            className="btnGhost sm danger"
                            style={isCanonical ? { opacity: 0.4 } : undefined}
                          >
                            {isRemoving ? (
                              <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faTrashCan} /> Remove
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="lwLinkForm">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#cdd5cf",
                  marginBottom: 12,
                }}
              >
                <FontAwesomeIcon icon={faLink} /> Manually link an additional
                wallet
              </div>
              <div className="lwLinkRow">
                <input
                  type="text"
                  className="input mono"
                  placeholder="0x…"
                  value={manualAddress}
                  onChange={(e) => setManualAddress(e.target.value)}
                  disabled={adding}
                  style={{ height: 46 }}
                />
                <input
                  type="text"
                  className="input mono"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="2FA code"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.replace(/\D/g, ""))}
                  disabled={adding}
                  style={{ height: 46, maxWidth: 120, textAlign: "center" }}
                />
                <button
                  type="button"
                  onClick={handleManualAdd}
                  disabled={adding}
                  className="btnPrimary"
                  style={{ height: 46 }}
                >
                  {adding ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} /> Add wallet
                    </>
                  )}
                </button>
              </div>
              <p className="lwSmall">
                Linking a wallet requires your 2FA code. Only EVM addresses are
                supported here. Wallets already associated with another partner
                cannot be linked.
              </p>
            </div>
          </div>

          <WalletCard
            title="Withdrawal wallet"
            description="Payouts are sent here in USDC on Ethereum, so this must be an EVM (0x…) wallet address. Change it from the Withdraw Wallet page — this page only displays it."
            address={activeWallet}
            emptyHint="No withdrawal wallet set"
            onViewHistory={() =>
              openHistory("Withdrawal wallet history", "btcWallet")
            }
            actionSlot={
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <Link
                  to="/edit-wallet"
                  onClick={() => dispatch(changePage("edit-wallet"))}
                  className="btnGhost sm accent"
                >
                  Manage withdrawal wallet →
                </Link>
              </div>
            }
          />
        </div>
      </div>

      <HistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={historyTitle}
        rows={auditItems}
        loading={walletAuditLoading}
        filterField={historyFilter}
      />
    </>
  );
};

export default LinkedWallets;
